import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon, Search, ArrowUpDown, MoreHorizontal, Anchor, Ship } from 'lucide-react';
import Button from '../components/Button';
import Input from "../components/ui/input";
import Label from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import Modal from '../components/ui/modal'; // Import the Modal component

const DetallesMovimientosPage = () => {
  const [date, setDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("todos");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [movimientos, setMovimientos] = useState([]);
  const [selectedMovimiento, setSelectedMovimiento] = useState(null); // State for selected movimiento
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility

  useEffect(() => {
    api.get('/movimientos')
      .then(response => {
        setMovimientos(response.data);
      })
      .catch(error => {
        console.error('Error fetching movements:', error);
      });
  }, []);

  // useEffect(() => {
  //   console.log(`Filter type changed to: ${filterType}`);
  // }, [filterType]);

  const filteredMovimientos = movimientos.filter(movimiento => {
    const matchesSearch = (String(movimiento.embarcacion?.numeroMatricula).toLowerCase().includes(searchTerm.toLowerCase())) ||
                          (movimiento.embarcacion?.tipoEmbarcacion && movimiento.embarcacion.tipoEmbarcacion.toLowerCase().includes(searchTerm.toLowerCase()));
    // const matchesType = filterType === "todos" || (movimiento.tipo_movimiento && movimiento.tipo_movimiento.toLowerCase() === filterType.toLowerCase());
    const matchesDate = !date || (movimiento.fechaHoraIngreso && format(new Date(movimiento.fechaHoraIngreso), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd'));
    return matchesSearch && matchesDate; // && matchesType;
  });

  const sortedMovimientos = [...filteredMovimientos].sort((a, b) => {
    if (!sortColumn) return 0;
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date) ? 'Fecha inválida' : format(date, 'dd/MM/yyyy HH:mm:ss');
  };

  const handleViewDetails = (movimiento) => {
    setSelectedMovimiento(movimiento);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMovimiento(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b text-white">
      <Navbar />
      <main className="container mx-auto mt-8 px-4">
        <h1 className="text-4xl text-azul-oscuro font-bold mb-8">Movimientos de Embarcaciones</h1>
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Buscar por embarcación o tipo"
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            { /* <div className="w-full md:w-48">
              <Label htmlFor="filter">Filtrar por tipo</Label>
              <Select value={filterType} onValueChange={(value) => setFilterType(value)}>
                <SelectTrigger id="filter">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="entrada">Entrada</SelectItem>
                  <SelectItem value="salida">Salida</SelectItem>
                </SelectContent>
              </Select>
            </div> */ }
            
            <div className="w-full md:w-48">
              <Label>Filtrar por fecha</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={`w-full justify-start text-left font-normal ${!date && "text-muted-foreground"}`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP", { locale: es }) : <span>Seleccionar fecha</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">
                <Button variant="ghost" onClick={() => handleSort('fechaHoraIngreso')}>
                  Fecha
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('embarcacion.numeroMatricula')}>
                  Embarcación
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('tipo_movimiento')}>
                  Tipo
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('embarcacion.tipoEmbarcacion')}>
                  Tipo de Embarcación
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('estado')}>
                  Estado
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedMovimientos.length > 0 ? (
              sortedMovimientos.map((movimiento) => (
                <TableRow key={movimiento.id}>
                  <TableCell className="font-medium">{formatDate(movimiento.fechaHoraIngreso)}</TableCell>
                  <TableCell>{movimiento.embarcacion?.numeroMatricula || 'N/A'}</TableCell>
                  <TableCell>
                    {movimiento.tipo_movimiento === 'entrada' ? (
                      <span className="flex items-center text-green-600">
                        <Anchor className="mr-1 h-4 w-4" />
                        Entrada
                      </span>
                    ) : (
                      <span className="flex items-center text-blue-600">
                        <Ship className="mr-1 h-4 w-4" />
                        Salida
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{movimiento.embarcacion?.tipoEmbarcacion || 'N/A'}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold
                      ${movimiento.estado === 'Completado' ? 'bg-green-100 text-green-800' :
                        movimiento.estado === 'En Proceso' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'}`}>
                      {movimiento.estado}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button className="h-8 w-8 p-0">
                          <span className="sr-only">Abrir menú</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                        <DropdownMenuItem>
                          <Button variant="yellow-gold" onClick={() => handleViewDetails(movimiento)}>
                            Ver detalles
                          </Button>
                        </DropdownMenuItem>
                        {/* <DropdownMenuItem>Editar movimiento</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600">Eliminar movimiento</DropdownMenuItem> */}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan="6" className="text-center">No se encontraron movimientos.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {isModalOpen && selectedMovimiento && (
          <Modal onClose={closeModal}>
            <div className="p-6 bg-verde-esmeralda rounded-lg shadow-lg max-w-lg mx-auto">
              <h2 className="text-2xl font-bold mb-4 text-azul-oscuro">Detalles del Movimiento</h2>
              <div className="space-y-2 text-blanco-puro">
                <p><strong>Matrícula:</strong> {selectedMovimiento.embarcacion?.numeroMatricula || 'N/A'}</p>
                <p><strong>Tipo de Embarcación:</strong> {selectedMovimiento.embarcacion?.tipoEmbarcacion || 'N/A'}</p>
                <p><strong>Nombre Fantasía:</strong> {selectedMovimiento.embarcacion?.nombreFantasia || 'N/A'}</p>
                <p><strong>Cliente:</strong> {selectedMovimiento.cliente?.nombre || 'N/A'} {selectedMovimiento.cliente?.apellido || ''}</p>
                <p><strong>DNI Cliente:</strong> {selectedMovimiento.cliente?.dni || 'N/A'}</p>
                <p><strong>Teléfono 1:</strong> {selectedMovimiento.cliente?.telefono1 || 'N/A'}</p>
                <p><strong>Teléfono 2:</strong> {selectedMovimiento.cliente?.telefono2 || 'N/A'}</p>
                <p><strong>Fecha y Hora de Ingreso:</strong> {formatDate(selectedMovimiento.fechaHoraIngreso)}</p>
                {selectedMovimiento.fechaHoraSalida && (
                  <p><strong>Fecha y Hora de Salida:</strong> {formatDate(selectedMovimiento.fechaHoraSalida)}</p>
                )}
                <p><strong>Estado:</strong> {selectedMovimiento.estado}</p>
              </div>
              <Button onClick={closeModal} className="mt-6 w-full bg-azul-oscuro text-blanco-puro hover:bg-verde-oscuro">Cerrar</Button>
            </div>
          </Modal>
        )}
      </main>
    </div>
  );
};

export default DetallesMovimientosPage;
